import mutationTypes from './mutationTypes'
import actionTypes from './actionTypes'
import getterTypes from './getterTypes'
import { Scan, SkillType } from '@/types'
import { Store } from 'vuex'
import { fetchScan, updateScan, fetchPowerEditData } from '@/api/v4/scans'
import { SKILL_TYPE } from '@/constants'
import { SCAN_DOC_TYPE } from '@/constants'
import { cloneDeep } from 'lodash'

interface ScanState {
  currentScan: Scan | null
  isFetching: boolean
  isEditingTitle: boolean
  showUploader: boolean
  showUpdateForm: boolean
  showEducationForm: boolean
  cvText: string | null
  jdText: string | null
  clText: string | null
  conversion_id: number | null
  coverletter_conversion_id: number | null
}

const rootState: ScanState = {
  currentScan: null,
  isFetching: false,
  isEditingTitle: false,
  showUploader: false,
  showUpdateForm: false,
  showEducationForm: false,
  cvText: null,
  jdText: null,
  clText: null,
  conversion_id: null,
  coverletter_conversion_id: null,
}

const mutations = {
  [mutationTypes.SET_SCAN.VALUE]: (state: ScanState, scan: Scan) => {
    state.currentScan = scan
  },
  [mutationTypes.SET_IS_STARRED.VALUE]: (
    state: ScanState,
    isStarred: boolean
  ) => {
    if (!state.currentScan) return

    state.currentScan.isStarred = isStarred
  },
  [mutationTypes.SET_TITLE.VALUE]: (state: ScanState, title: string) => {
    if (!state.currentScan) return

    state.currentScan.metadata.title = title
  },
  [mutationTypes.SET_IS_FETCHING.VALUE]: (
    state: ScanState,
    isFetching: boolean
  ) => {
    state.isFetching = isFetching
  },
  [mutationTypes.SET_IS_EDITING_TITLE.VALUE]: (
    state: ScanState,
    isEditingTitle: boolean
  ) => {
    state.isEditingTitle = isEditingTitle
  },
  [mutationTypes.SET_SCAN_DEFAULT.VALUE]: (state: ScanState) => {
    state.currentScan = null
  },
  [mutationTypes.SET_SHOW_UPDATE_FORM.VALUE]: (
    state: ScanState,
    showUpdateForm: boolean
  ) => {
    state.showUpdateForm = showUpdateForm
  },
  [mutationTypes.SET_SHOW_EDUCATION_FORM.VALUE]: (
    state: ScanState,
    showEducationForm: boolean
  ) => {
    state.showEducationForm = showEducationForm
  },
  [mutationTypes.SET_POWER_EDIT_DATA.VALUE]: (state: ScanState, delta: any) => {
    if (!state.currentScan?.delta) return
    state.currentScan.delta = delta
  },
  [mutationTypes.SET_OPPORTUNITY_ID.VALUE]: (
    state: ScanState,
    opportunityId
  ) => {
    const scan = cloneDeep(state.currentScan)
    if (scan?.metadata?.jobDescription) {
      scan.metadata.jobDescription.opportunity = opportunityId
    }
    state.currentScan = scan
  },
  [mutationTypes.SET_CV_TEXT.VALUE]: (state: ScanState, value) => {
    state.cvText = value
  },
  [mutationTypes.SET_JD_TEXT.VALUE]: (state: ScanState, value) => {
    state.jdText = value
  },
  [mutationTypes.SET_CL_TEXT.VALUE]: (state: ScanState, value) => {
    state.clText = value
  },
  [mutationTypes.SET_CONVERSION_ID.VALUE]: (state: ScanState, value) => {
    state.conversion_id = value
  },
  [mutationTypes.SET_CL_CONVERSION_ID.VALUE]: (state: ScanState, value) => {
    state.coverletter_conversion_id = value
  },
}

const actions = {
  [actionTypes.FETCH_SCAN_RESULT.VALUE]: async (
    { commit, state }: Store<ScanState>,
    { scanId, forceRefresh }
  ) => {
    if (!scanId || scanId === 'undefined') return
    if (
      !forceRefresh &&
      Number(scanId) === state.currentScan?.metadata?.scan?.id
    ) {
      return
    }
    commit(mutationTypes.SET_IS_FETCHING.VALUE, true)
    commit(mutationTypes.SET_SCAN.VALUE, null)
    const { data, error } = await fetchScan(scanId)
    commit(mutationTypes.SET_SCAN.VALUE, data)
    commit(mutationTypes.SET_IS_FETCHING.VALUE, false)
    commit(mutationTypes.SET_IS_STARRED.VALUE, data?.isStarred || false)
  },
  [actionTypes.UPDATE_STARRED.VALUE]: async (
    { state, commit, dispatch }: Store<ScanState>,
    isStarred: boolean
  ) => {
    const scanId = state.currentScan?.metadata?.scan?.id
    if (!scanId) return
    commit(mutationTypes.SET_IS_STARRED.VALUE, isStarred)
    const { data, error } = await updateScan(scanId, {
      starredState: isStarred,
    })
    if (error) {
      commit(mutationTypes.SET_IS_STARRED.VALUE, !isStarred)
      return
    }
  },
  [actionTypes.FETCH_POWER_EDIT_DATA.VALUE]: async (
    { commit }: Store<ScanState>,
    scanId: number
  ) => {
    const { data, error } = await fetchPowerEditData(scanId)
    commit(mutationTypes.SET_POWER_EDIT_DATA.VALUE, data)
  },
}

const getters = {
  [getterTypes.CURRENT_SCAN.VALUE]: (state: ScanState) => {
    return state.currentScan
  },
  [getterTypes.MATCH_RATE.VALUE]: (state: ScanState) => {
    return state.currentScan?.matchRate || null
  },
  [getterTypes.RESUME_VARIABLES.VALUE]: (state: ScanState) => {
    return state.currentScan?.metadata?.variables || []
  },
  [getterTypes.FINDINGS_BY_CATEGORY.VALUE]: (state: ScanState) => category => {
    return (
      state.currentScan?.findings?.filter(
        finding => finding.category === category
      ) || []
    )
  },
  [getterTypes.FINDINGS_FILTERED.VALUE]:
    (state: ScanState) =>
    (documentType: string = SCAN_DOC_TYPE.RESUME, category: string) => {
      const findings = state.currentScan?.findings?.filter(finding => {
        const matchesCategory = finding.category === category
        const matchesDocType = finding.documentType === documentType

        return matchesCategory && matchesDocType
      })

      return findings || []
    },
  [getterTypes.IS_STARRED.VALUE]: (state: ScanState) => {
    return state.currentScan?.isStarred || false
  },
  [getterTypes.IS_FETCHING.VALUE]: (state: ScanState) => {
    return state.isFetching
  },
  [getterTypes.IS_EDITING_TITLE.VALUE]: (state: ScanState) => {
    return state.isEditingTitle
  },
  [getterTypes.TITLE.VALUE]: (state: ScanState) => {
    return state.currentScan?.metadata.title
  },
  [getterTypes.SKILLS.VALUE]: (state: ScanState) => (type?: SkillType) => {
    const skills = state.currentScan?.skills
    const keywords = state.currentScan?.keywords

    if (keywords && type === SKILL_TYPE.OTHER) {
      return keywords
    }

    if (!skills) return []

    if (type) {
      return skills[type]
    }

    return skills.other.concat(skills.soft).concat(skills.hard)
  },
  [getterTypes.RESUME.VALUE]: (state: ScanState) => {
    return state.currentScan?.resume
  },
  [getterTypes.COVER_LETTER.VALUE]: (state: ScanState) => {
    return state.currentScan?.coverLetter
  },
  [getterTypes.JOB_DESCRIPTION.VALUE]: (state: ScanState) => {
    return state.currentScan?.jobDescription
  },
  [getterTypes.CURRENT_JD.VALUE]: (state: ScanState) => {
    return state.currentScan?.metadata.jobDescription
  },
  [getterTypes.SHOW_UPLOADER.VALUE]: (state: ScanState) => {
    return state.showUploader
  },
  [getterTypes.SHOW_UPDATE_FORM.VALUE]: (state: ScanState) => {
    return state.showUpdateForm
  },
  [getterTypes.SHOW_EDUCATION_FORM.VALUE]: (state: ScanState) => {
    return state.showEducationForm
  },
  [getterTypes.POWER_EDIT_DATA.VALUE]: (state: ScanState) => {
    return state.currentScan?.delta || []
  },
  [getterTypes.CV_TEXT.VALUE]: (state: ScanState) => {
    return state.cvText
  },
  [getterTypes.JD_TEXT.VALUE]: (state: ScanState) => {
    return state.jdText
  },
  [getterTypes.CL_TEXT.VALUE]: (state: ScanState) => {
    return state.clText
  },
  [getterTypes.CONVERSION_ID.VALUE]: (state: ScanState) => {
    return state.conversion_id
  },
  [getterTypes.CL_CONVERSION_ID.VALUE]: (state: ScanState) => {
    return state.coverletter_conversion_id
  },
}

export default {
  namespaced: true,
  state: rootState,
  mutations,
  actions,
  getters,
}
